/**
 * Theme Config — compact color-scheme, accent-color and corner controls for the
 * schmancy theme system.
 *
 * Drives the schmancy `theme` service directly (that IS its job): the scheme
 * chips call `theme.setScheme`, the accent swatches (plus an optional trailing
 * native custom-color picker) call `theme.setColor`, and both mirror
 * `theme.scheme$` / `theme.color$` so the control reflects the live
 * device preference — including changes made from another surface. Those two
 * knobs persist to schmancy's own `schmancy/theme` device pref, so a user's
 * choice survives reload wherever a `<schmancy-theme>` root is mounted.
 * Corner rounding drives the shared `radius` service and its device preference.
 *
 * App-agnostic. The accent palette is a prop (`colors`) with a tasteful default;
 * a consuming app passes its own brand-first set. The header label is a prop too
 * (`label`, empty string → no header). The host is a plain transparent block —
 * it paints no surface of its own, so it drops seamlessly into a menu, a settings
 * panel, or a card the consumer wraps it in.
 *
 *   <schmancy-theme-config .colors=${BRAND_ACCENTS}></schmancy-theme-config>
 */
import '@mhmo91/schmancy/form/fields/chips'
import { theme } from '@mhmo91/schmancy/theme'
import { radius } from '@mhmo91/schmancy/radius'
import { SchmancyElement, schmancyElement } from '@mhmo91/schmancy/mixins'
import { css, html, nothing } from 'lit'
import { property, state } from 'lit/decorators.js'
import { repeat } from 'lit/directives/repeat.js'
import { auditTime, filter, merge, Subject, takeUntil, tap } from 'rxjs'

export interface ThemeAccent {
	/** Human label — the swatch's accessible name. */
	name: string
	/** Source hex the schmancy theme expands into its semantic palette. */
	hex: string
}

/** Generic seeds. A brand app overrides `colors` with its own set. */
export const DEFAULT_ACCENTS: ThemeAccent[] = [
	{ name: 'Violet', hex: '#6750A4' },
	{ name: 'Ocean', hex: '#1E6FA8' },
	{ name: 'Teal', hex: '#1C7C74' },
	{ name: 'Forest', hex: '#3B7A57' },
	{ name: 'Rose', hex: '#B3425A' },
	{ name: 'Amber', hex: '#C88A2E' },
]

const SCHEMES = [
	{ value: 'auto', icon: 'brightness_auto' },
	{ value: 'light', icon: 'light_mode' },
	{ value: 'dark', icon: 'dark_mode' },
] as const

/** Scheme chip labels. Override to localize — defaults to English. */
export interface ThemeSchemeLabels {
	auto: string
	light: string
	dark: string
}

export const DEFAULT_SCHEME_LABELS: ThemeSchemeLabels = { auto: 'Auto', light: 'Light', dark: 'Dark' }

@schmancyElement('schmancy-theme-config')
export class SchmancyThemeConfig extends SchmancyElement {
	static styles = [
		css`
			:host {
				display: block;
			}
			.tc-rainbow {
				position: absolute;
				inset: 0;
				display: flex;
				align-items: center;
				justify-content: center;
				background: conic-gradient(from 90deg, #ff4d4d, #ffd24d, #4dff88, #4dd2ff, #4d4dff, #ff4df2, #ff4d4d);
			}
			.tc-custom-input {
				position: absolute;
				inset: 0;
				width: 100%;
				height: 100%;
				margin: 0;
				padding: 0;
				border: none;
				opacity: 0;
				cursor: pointer;
			}
		`,
	]

	/** Accent swatches, in display order. Defaults to a generic color set. */
	@property({ attribute: false }) colors: ThemeAccent[] = DEFAULT_ACCENTS

	/** Header label; empty string renders no header. */
	@property() label = 'Theme'

	/** Scheme chip labels (Auto/Light/Dark). Override to localize — defaults to English. */
	@property({ attribute: false }) schemeLabels: ThemeSchemeLabels = DEFAULT_SCHEME_LABELS

	/** Corner scale label, including its percent unit. Override to localize. */
	@property() radiusLabel = 'Corner rounding (%)'

	/** Show a native custom-color picker after the swatches. */
	@property({ type: Boolean }) custom = true

	@state() private scheme = theme.scheme
	@state() private color = theme.color
	@state() private radiusBase = radius.base

	private readonly customColor$ = new Subject<string>()

	override connectedCallback() {
		super.connectedCallback()

		merge(
			theme.scheme$.pipe(tap(scheme => {
				this.scheme = scheme
			})),
			theme.color$.pipe(tap(color => {
				this.color = color
			})),
			theme.themeComponent$.pipe(
				filter(component => component !== null),
				tap(component => { this.color = component.color }),
			),
			this.customColor$.pipe(auditTime(50), tap(color => theme.setColor(color))),
			radius.base$.pipe(tap(base => { this.radiusBase = base })),
		).pipe(takeUntil(this.disconnecting)).subscribe()
	}

	override render() {
		return html`
			<div class="flex flex-col gap-3">
				${
					this.label
						? html`
								<div class="flex items-center gap-3">
									<schmancy-icon class="text-surface-onVariant text-base">palette</schmancy-icon>
									<schmancy-typography type="body" token="sm" class="text-surface-onVariant flex-1"
										>${this.label}</schmancy-typography
									>
								</div>
							`
						: nothing
				}

				<schmancy-chips
					class="w-full"
					required
					.value=${this.scheme}
					@change=${(e: CustomEvent<{ value: string }>) => {
						const value = e.detail.value
						if (value === 'auto' || value === 'light' || value === 'dark') theme.setScheme(value)
					}}
				>
					${repeat(
						SCHEMES,
						s => s.value,
						s => html`
							<schmancy-filter-chip size="xs" .value=${s.value}>
								<schmancy-icon slot="leading" class="text-sm">${s.icon}</schmancy-icon>
								${this.schemeLabels[s.value]}
							</schmancy-filter-chip>
						`,
					)}
				</schmancy-chips>

				<div class="flex flex-wrap gap-2.5" role="group" aria-label="Accent color">
					${repeat(
						this.colors,
						a => a.hex,
						a => {
							const active = a.hex.toLowerCase() === this.color.toLowerCase()
							// Ring in the neutral foreground token contrasts on any background
							// AND against the swatch's own hue (a primary-colored ring would
							// vanish against the accent it selects).
							return html`
								<button
									type="button"
									style="background:${a.hex}"
									aria-label=${`${a.name} accent color`}
									aria-pressed=${active}
									title=${a.name}
									class=${this.classMap({
										'w-7 h-7 rounded-full cursor-pointer transition-transform duration-150': true,
										'ring-2 ring-offset-2 ring-offset-surface-default ring-surface-on scale-110': active,
										'hover:scale-110': !active,
									})}
									@click=${() => theme.setColor(a.hex)}
								></button>
							`
						},
					)}
					${
						this.custom
							? (() => {
									const isCustom = !this.colors.some(a => a.hex.toLowerCase() === this.color.toLowerCase())
									return html`
										<span
											class=${this.classMap({
												'relative inline-flex w-7 h-7 rounded-full overflow-hidden cursor-pointer transition-transform duration-150': true,
												'ring-2 ring-offset-2 ring-offset-surface-default ring-surface-on scale-110': isCustom,
												'hover:scale-110': !isCustom,
											})}
											style=${isCustom ? `background:${this.color}` : ''}
										>
											${
												isCustom
													? nothing
													: html`<span class="tc-rainbow"
															><schmancy-icon class="text-sm text-white">add</schmancy-icon></span
														>`
											}
											<input
												class="tc-custom-input"
												type="color"
												.value=${this.color}
												aria-label="Custom accent color"
												title="Custom color"
												@input=${(e: Event) => this.customColor$.next((e.target as HTMLInputElement).value)}
												@change=${(e: Event) => theme.setColor((e.target as HTMLInputElement).value)}
											/>
										</span>
									`
								})()
							: nothing
					}
				</div>
				<schmancy-range
					.label=${this.radiusLabel}
					min="0"
					max="200"
					step="5"
					size="comfortable"
					.value=${this.radiusBase / radius.defaultBase * 100}
					@change=${(event: CustomEvent<{ value: number }>) => radius.setBase(event.detail.value / 100 * radius.defaultBase)}
				></schmancy-range>
			</div>
		`
	}
}

declare global {
	interface HTMLElementTagNameMap {
		'schmancy-theme-config': SchmancyThemeConfig
	}
}
